import { ContentService, type TopicPublic } from "@/client"
import { Field } from "@/components/ui/field"
import { InputGroup } from "@/components/ui/input-group"
import { useDebounce } from "@/hooks/useDebounce"
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  NativeSelectField,
  NativeSelectRoot,
  Stack,
  Tabs,
  Text,
} from "@chakra-ui/react"
import { useQueries, useQuery } from "@tanstack/react-query"
import { useRef, useState } from "react"
import { FiSearch, FiX } from "react-icons/fi"

const DEBOUNCE_MS = 300
const MIN_SUGGESTION_LENGTH = 3
/**
 * Full-text search over the whole bank is expensive, and one- or two-character
 * terms match so much that the result is useless anyway. Applies to the text
 * portion only — chips are their own, always-valid criterion.
 */
const MIN_QUERY_LENGTH = 3
const SUGGESTION_LIMIT = 10
/** There are only a few dozen subjects org-wide, so they are fetched whole. */
const SUBJECT_LIMIT = 100

const kindOptions = (flashcardsEnabled: boolean) => [
  { value: "all", label: "All" },
  { value: "question", label: "Questions" },
  ...(flashcardsEnabled ? [{ value: "flashcard", label: "Flashcards" }] : []),
]

/** The result kinds the bank can be narrowed to; "all" sends no filter. */
export type SearchKind = "all" | "question" | "flashcard"

export interface SearchBarProps {
  q: string
  topicIds: string[]
  subjectIds: string[]
  kind: SearchKind
  /**
   * False when the org has flashcards switched off. The backend already
   * suppresses flashcard rows for those orgs, so offering the filter would
   * only promise results that can never arrive.
   */
  flashcardsEnabled: boolean
  onQueryChange: (q: string) => void
  onTopicIdsChange: (topicIds: string[]) => void
  onSubjectIdsChange: (subjectIds: string[]) => void
  onKindChange: (kind: SearchKind) => void
}

/**
 * Query input, topic suggestions, subject selector, the result-kind filter and
 * the active-filter chips.
 *
 * The text query is explicit: typing only updates local state, and the value
 * reaches `onQueryChange` when the user submits the form (Enter, or the Search
 * button). Chips and the kind filter are deliberate clicks, so they still
 * apply immediately.
 *
 * The topic suggestion dropdown is the one thing that still follows the
 * keystrokes — it is a debounced lookup capped at `SUGGESTION_LIMIT` rows, and
 * without it the chips would be undiscoverable.
 */
export function SearchBar({
  q,
  topicIds,
  subjectIds,
  kind,
  flashcardsEnabled,
  onQueryChange,
  onTopicIdsChange,
  onSubjectIdsChange,
  onKindChange,
}: SearchBarProps) {
  const [inputValue, setInputValue] = useState(q)
  const [showMinLengthHint, setShowMinLengthHint] = useState(false)
  // The last value this component handed to `onQueryChange`. The parent turns
  // that into a URL change and hands it straight back as a new `q`, and that
  // echo must not be mistaken for an external navigation.
  const lastPropagated = useRef(q)
  const [syncedQ, setSyncedQ] = useState(q)

  // `q` can change without a remount — browser Back/Forward, or a programmatic
  // navigate. The input is initialized from it once, so without this the
  // textbox (and the topic suggestions keyed off it) would keep showing the
  // old text while the results reflect the restored URL.
  //
  // Adjusting during render rather than in an effect keeps the restored value
  // visible in the very commit that carries it, so a submit can never race a
  // pending effect and propagate the text the navigation just replaced.
  if (q !== syncedQ) {
    setSyncedQ(q)
    // Our own submit comes back as a `q` change too; treating that echo as
    // external would re-seed the input from a value the user has already typed
    // past. Writing the ref here is idempotent, so a repeated render cannot
    // change the outcome.
    if (q !== lastPropagated.current) {
      lastPropagated.current = q
      setInputValue(q)
      setShowMinLengthHint(false)
    }
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmed = inputValue.trim()
    // An empty text box is a valid submit: the chips may be carrying the
    // search on their own, and clearing the text has to be able to reach the
    // parent so the results drop the keyword.
    if (trimmed.length > 0 && trimmed.length < MIN_QUERY_LENGTH) {
      setShowMinLengthHint(true)
      return
    }
    setShowMinLengthHint(false)
    lastPropagated.current = inputValue
    onQueryChange(inputValue)
  }

  const debouncedInput = useDebounce(inputValue, DEBOUNCE_MS)
  const suggestionTerm = debouncedInput.trim()
  const suggestionsEnabled = suggestionTerm.length >= MIN_SUGGESTION_LENGTH

  const { data: suggestions } = useQuery({
    queryKey: ["search-topic-autocomplete", suggestionTerm],
    queryFn: () =>
      ContentService.autocompleteTopics({
        q: suggestionTerm,
        limit: SUGGESTION_LIMIT,
      }),
    enabled: suggestionsEnabled,
    staleTime: 60 * 1000,
  })

  const { data: subjectsData } = useQuery({
    queryKey: ["subjects", SUBJECT_LIMIT],
    queryFn: () => ContentService.listSubjects({ limit: SUBJECT_LIMIT }),
    staleTime: 30 * 60 * 1000,
  })
  const subjects = subjectsData?.data ?? []

  // Chip labels come from an accumulating cache, never from the live suggestion
  // list. Deriving them from `suggestions` would blank a chip the moment the
  // user typed something else, and would leave every chip nameless when the
  // page is opened from a bookmarked URL that carries only topic ids.
  const topicNames = useRef(new Map<string, string>())
  for (const topic of suggestions ?? []) {
    topicNames.current.set(topic.id, topic.name)
  }

  // Fallback for ids no suggestion has ever named — the bookmarked-URL case.
  // Every selected id keeps a mounted query so a resolved name is never evicted
  // and then re-requested; `enabled` stops it fetching once the name is known.
  const namedTopicQueries = useQueries({
    queries: topicIds.map((topicId) => ({
      queryKey: ["topic", topicId],
      queryFn: () => ContentService.getTopic({ topicId }),
      enabled: !topicNames.current.has(topicId),
      staleTime: Number.POSITIVE_INFINITY,
      retry: false,
    })),
  })
  for (const result of namedTopicQueries) {
    if (result.data) {
      topicNames.current.set(result.data.id, result.data.name)
    }
  }

  const topicLabel = (topicId: string) =>
    topicNames.current.get(topicId) ?? "Unnamed topic"
  const subjectLabel = (subjectId: string) =>
    subjects.find((subject) => subject.id === subjectId)?.name ??
    "Unnamed subject"

  const addTopic = (topic: TopicPublic) => {
    topicNames.current.set(topic.id, topic.name)
    if (topicIds.includes(topic.id)) return
    onTopicIdsChange([...topicIds, topic.id])
  }

  const removeTopic = (topicId: string) => {
    onTopicIdsChange(topicIds.filter((id) => id !== topicId))
  }

  const toggleSubject = (subjectId: string) => {
    onSubjectIdsChange(
      subjectIds.includes(subjectId)
        ? subjectIds.filter((id) => id !== subjectId)
        : [...subjectIds, subjectId],
    )
  }

  const visibleSuggestions = suggestionsEnabled
    ? (suggestions ?? []).filter((topic) => !topicIds.includes(topic.id))
    : []

  const hasChips = topicIds.length > 0 || subjectIds.length > 0

  return (
    <Stack gap={3}>
      <Flex gap={4} flexDir={{ base: "column", md: "row" }} align="flex-start">
        <Box position="relative" flex="1" width="100%">
          <form onSubmit={handleSubmit}>
            <Field label="Search">
              <Flex gap={2} width="100%" align="center">
                <InputGroup flex="1" startElement={<FiSearch />}>
                  <Input
                    value={inputValue}
                    onChange={(event) => {
                      setInputValue(event.target.value)
                      setShowMinLengthHint(false)
                    }}
                    placeholder="Search questions and flashcards"
                    aria-label="Search the question bank"
                  />
                </InputGroup>
                <Button type="submit" flexShrink={0}>
                  Search
                </Button>
              </Flex>
            </Field>
          </form>
          {showMinLengthHint && (
            <Text fontSize="xs" color="fg.error" mt={1} role="alert">
              Type at least {MIN_QUERY_LENGTH} characters to search
            </Text>
          )}
          {visibleSuggestions.length > 0 && (
            <Box
              role="listbox"
              aria-label="Topic suggestions"
              position="absolute"
              zIndex="dropdown"
              mt={1}
              width="100%"
              bg="bg.panel"
              borderWidth="1px"
              borderRadius="md"
              boxShadow="md"
              maxH="260px"
              overflowY="auto"
            >
              <Stack gap={0} p={1}>
                {visibleSuggestions.map((topic) => (
                  <Button
                    key={topic.id}
                    role="option"
                    size="sm"
                    variant="ghost"
                    justifyContent="flex-start"
                    onClick={() => addTopic(topic)}
                  >
                    {topic.name}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}
        </Box>

        <Field label="Subject" width={{ base: "100%", md: "260px" }}>
          <NativeSelectRoot>
            <NativeSelectField
              value=""
              aria-label="Filter by subject"
              onChange={(event) => {
                const subjectId = event.target.value
                if (subjectId) toggleSubject(subjectId)
              }}
            >
              <option value="">Add a subject…</option>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.name}
                </option>
              ))}
            </NativeSelectField>
          </NativeSelectRoot>
        </Field>
      </Flex>

      {/*
        `enclosed` at `sm` gives a segmented-control weight, so the filter
        reads as a control beside the input rather than a page-level tab
        strip. There are no `Tabs.Content` panels: the results below are the
        panel, and rendering them inside one would remount the list on every
        switch.
      */}
      <Tabs.Root
        size="sm"
        variant="enclosed"
        value={kind}
        onValueChange={({ value }) => {
          onKindChange(value as SearchKind)
        }}
      >
        <Tabs.List aria-label="Filter by result type">
          {kindOptions(flashcardsEnabled).map((option) => (
            <Tabs.Trigger key={option.value} value={option.value}>
              {option.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      </Tabs.Root>

      {hasChips && (
        <Flex gap={2} wrap="wrap" aria-label="Active filters">
          {subjectIds.map((subjectId) => (
            <FilterChip
              key={subjectId}
              label={subjectLabel(subjectId)}
              colorPalette="teal"
              onRemove={() => toggleSubject(subjectId)}
            />
          ))}
          {topicIds.map((topicId) => (
            <FilterChip
              key={topicId}
              label={topicLabel(topicId)}
              colorPalette="purple"
              onRemove={() => removeTopic(topicId)}
            />
          ))}
        </Flex>
      )}
    </Stack>
  )
}

function FilterChip({
  label,
  colorPalette,
  onRemove,
}: {
  label: string
  colorPalette: string
  onRemove: () => void
}) {
  return (
    <Badge colorPalette={colorPalette} variant="subtle" gap={1} pl={2} pr={1}>
      <Text as="span">{label}</Text>
      <IconButton
        aria-label={`Remove ${label}`}
        size="2xs"
        variant="ghost"
        colorPalette={colorPalette}
        onClick={onRemove}
      >
        <FiX />
      </IconButton>
    </Badge>
  )
}
